import { memo, useState, useCallback, type ComponentProps } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import LooseLeafModal from "common/meeting/notary/document/navigator/loose_leaf_modal";
import type { NotarialActs, LooseLeafCertificates, PageTypes } from "graphql_globals";
import { getCurrentDocumentNode, getCurrentPenholderInSignerParties } from "common/meeting/util";
import { REQUEST_IN_MEETING_DOCUMENT_UPLOAD } from "constants/signer_capabilities";
import Icon from "common/core/icon";
import Button from "common/core/button";
import { useJumpToLooseLeaf } from "common/meeting/scroll";
import DocumentLockControls from "common/meeting/notary/document/navigator/lock";
import SignerActionsCount from "common/meeting/notary/document/navigator/signer_actions_count";

import Styles from "./index.module.scss";
import DocumentListing from "./document";
import InMeetingRequestUploadButton, {
  useDocumentUploadNotification,
} from "./request_in_meeting_upload";
import { AddLooseLeafButton, useLooseLeafMutateFn, useAvailableLooseLeafs } from "./loose_leaf";
import type { NotaryMeetingBundleNav as Meeting } from "./index_fragment.graphql";

type ListingProps = ComponentProps<typeof DocumentListing>;
type Props = {
  meeting: Meeting;
  notarialActLooseLeafMapping: Parameters<typeof useAvailableLooseLeafs>[0];
  onSelectDocument: ListingProps["onSelectDocument"];
  onSelectLooseLeaf: ListingProps["onSelectLooseLeaf"];
  quickstampHelpState:
    | null
    | { type: "pre-placement-help" }
    | { type: "does-it-fit"; annotationIds: string[] };
  onQuickstampHelpMoveToLooseLeaf: (annotationIds: string[]) => undefined | NotarialActs;
  onQuickstampHelpExit: () => void;
};

function BundleNavigator({
  meeting,
  onSelectLooseLeaf,
  onSelectDocument,
  notarialActLooseLeafMapping,
  quickstampHelpState,
  onQuickstampHelpMoveToLooseLeaf,
  onQuickstampHelpExit,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [LLPModalOpen, setLLPModalOpen] = useState<boolean | { initLLPSelection: NotarialActs }>(
    false,
  );
  const currentDocumentNode = getCurrentDocumentNode(meeting);
  const currentDocumentId = currentDocumentNode.id;
  const { name, versionedLooseLeafCertificates } = currentDocumentNode;
  const looseLeafMutateFn = useLooseLeafMutateFn(meeting);
  useDocumentUploadNotification(onSelectDocument);
  const jumpToLooseLeaf = useJumpToLooseLeaf(currentDocumentId);
  const handleSaveLLPs = useCallback<ComponentProps<typeof LooseLeafModal>["onSave"]>(
    (selectedLLPs, newestLLP) => {
      return looseLeafMutateFn(selectedLLPs).then(() => {
        if (newestLLP) {
          jumpToLooseLeaf({
            documentId: currentDocumentId,
            pageType: newestLLP as unknown as PageTypes,
          });
        }
        setLLPModalOpen(false);
      });
    },
    [looseLeafMutateFn, currentDocumentId, jumpToLooseLeaf],
  );
  const handleRemoveLooseLeaf = useCallback(
    ({ actType }: { actType: LooseLeafCertificates }) => {
      const newSelectedLLPs = new Set(
        versionedLooseLeafCertificates.map((llp) => llp.actType).filter((type) => type !== actType),
      );
      return looseLeafMutateFn(newSelectedLLPs);
    },
    [versionedLooseLeafCertificates],
  );
  const activeSignerPartyParticipant = getCurrentPenholderInSignerParties(meeting);
  const showInMeetingRequestButton =
    meeting.documentBundle!.signerCanUpdate &&
    activeSignerPartyParticipant.__typename === "SignerParticipant" &&
    meeting.signerCapabilities!.includes(REQUEST_IN_MEETING_DOCUMENT_UPLOAD);
  const handleLock = () => {
    const documentEdges = meeting.documentBundle!.documents.edges;
    const curDocumentIndex = documentEdges.findIndex((e) => e.node.id === currentDocumentNode.id);
    const nextDocument = documentEdges[curDocumentIndex + 1] as
      | undefined
      | (typeof documentEdges)[number];
    if (nextDocument) {
      onSelectDocument(nextDocument.node);
    }
  };
  const availableLooseLeafs = useAvailableLooseLeafs(notarialActLooseLeafMapping, meeting);
  return (
    <header>
      <div className={Styles.navigator}>
        <nav className={Styles.controls}>
          <button
            type="button"
            onClick={() => setIsOpen((o) => !o)}
            className={classnames(Styles.dropdown, isOpen && Styles.isOpen)}
          >
            {name}
            <Icon name="caret-down-small" />
          </button>
          <AddLooseLeafButton onClick={() => setLLPModalOpen(true)} />
          {LLPModalOpen && (
            <LooseLeafModal
              initLLP={LLPModalOpen === true ? undefined : LLPModalOpen.initLLPSelection}
              availableLooseLeafs={availableLooseLeafs}
              currentDocumentNode={currentDocumentNode}
              onClose={() => setLLPModalOpen(false)}
              onSave={handleSaveLLPs}
            />
          )}
          {showInMeetingRequestButton && "signerIdentityId" in activeSignerPartyParticipant && (
            <InMeetingRequestUploadButton
              meetingId={meeting.id}
              activeParticipantId={activeSignerPartyParticipant.signerIdentityId}
            />
          )}
          <SignerActionsCount
            currentDocumentNode={currentDocumentNode}
            meetingParticipants={meeting.meetingParticipants}
          />
          <DocumentLockControls meeting={meeting} onLock={handleLock} />
        </nav>
        {isOpen && (
          <div className={Styles.documentListingWrapper}>
            <DocumentListing
              meeting={meeting}
              onSelectDocument={onSelectDocument}
              onRemoveLooseLeaf={handleRemoveLooseLeaf}
              onSelectLooseLeaf={onSelectLooseLeaf}
            />
          </div>
        )}
      </div>
      {quickstampHelpState && (
        <div className={Styles.quickstampHelp}>
          <Icon name="faq" />
          {quickstampHelpState.type === "pre-placement-help" ? (
            <FormattedMessage
              id="a9be2e38-d55e-4e4f-acc5-0fa55765818e"
              defaultMessage="Make sure Quick Stamp doesn't overlap any contents of the document"
              tagName="p"
            />
          ) : (
            <>
              <FormattedMessage
                id="8c8d785d-8f2b-4d3a-86e0-2132168a14b8"
                defaultMessage="Is there enough room for the Quick Stamp?"
                tagName="p"
              />
              <Button buttonColor="action" variant="primary" onClick={onQuickstampHelpExit}>
                <FormattedMessage id="9a4f1338-dd2d-474f-b2fd-54f136508da4" defaultMessage="Yes" />
              </Button>
              <Button
                variant="tertiary"
                buttonColor="action"
                onClick={() => {
                  const initLLPSelection = onQuickstampHelpMoveToLooseLeaf(
                    quickstampHelpState.annotationIds,
                  );
                  setLLPModalOpen(initLLPSelection ? { initLLPSelection } : true);
                }}
              >
                <FormattedMessage
                  id="524f3d1b-a2f8-4f4c-94f8-2c0c81fc6d5c"
                  defaultMessage="No, move to loose leaf"
                />
              </Button>
              <Icon name="x" onClick={onQuickstampHelpExit} />
            </>
          )}
        </div>
      )}
    </header>
  );
}

export default memo(BundleNavigator);
